import React, {useContext, useEffect, useState} from "react";
import {doc, onSnapshot} from "firebase/firestore";
import {Box, CircularProgress, Stack, Typography} from "@mui/material";
import {db} from "../../../config/firebase";
import {PROJECTS_COLLECTION} from "../../../db/collections";
import {buildFlowVariables, Context as AppContext} from "../../../context/AppContext";
import {useRegistryProject} from "../RegistryProjectContext";
import {Project} from "../../project/flow/Project";
import {SubProjectSelect} from "../SubProjectSelect";
import {subProjectLabel} from "../../../utils/subProjects";

// Flow designer of the selected sub project. Every sub project has its own Firebase flow (subProject.flowUid)
// holding the flows, intents, entities, ... it runs; the designer edits that document live, like the legacy
// /project/{flowUid}/flow page. A flow of it opens at {basePath}/flow/{flowId}.
export const RegistryDesignerPage = () => {
  const {project, subProject, basePath} = useRegistryProject();
  const {getAllServersWithDetails, getAllKbTopics} = useContext(AppContext);
  const flowUid = subProject.flowUid;
  const [flowProject, setFlowProject] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setFlowProject(null);
    setMissing(false);
    if (!flowUid) return;
    // live: the editor saves through updateProject and redraws from this snapshot
    const unsubscribe = onSnapshot(doc(db, PROJECTS_COLLECTION, flowUid), (snapshot) => {
      const data = snapshot.data();
      if (!data) {
        setMissing(true);
        return;
      }
      const next = {...data, id: flowUid};
      next.flow_variables = buildFlowVariables(next);
      setFlowProject(next);
    });
    return () => unsubscribe();
  }, [flowUid]);

  // MCP servers and knowledge-base topics are offered in the node editors
  const mcpRegistry = flowProject?.configuration?.mcp_registry;
  const organization = flowProject?.settings?.organization;
  useEffect(() => {
    if (mcpRegistry) getAllServersWithDetails({flowUid, projectId: project.id, subProjectId: subProject.id});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mcpRegistry, flowUid]);
  useEffect(() => {
    if (organization) getAllKbTopics(organization);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  if (!flowUid || missing) {
    return (
      <Stack alignItems={"center"} justifyContent={"center"} gap={0.5} sx={{flex: 1, p: 4, textAlign: "center"}}>
        <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D"}}>
          {subProjectLabel(subProject) || "This sub project"} has no flow yet
        </Typography>
        <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>The designer edits the sub project's flow, so it is available once one exists.</Typography>
        <Box sx={{mt: 1.5}}><SubProjectSelect/></Box>
      </Stack>
    );
  }

  if (!flowProject) {
    return (
      <Stack alignItems={"center"} justifyContent={"center"} sx={{flex: 1}}>
        <CircularProgress size={28} sx={{color: "#8491F7"}}/>
      </Stack>
    );
  }

  return (
    <Box sx={{
      flex: 1, minHeight: 0, height: "100%", display: "flex", p: 2,
      // the editor in the dashboard style: the side panel and the canvas as separate white cards
      "& > .MuiStack-root": {gap: 2, minHeight: 0},
      "& [data-tour='flow-canvas']": {
        borderRadius: "18px", overflow: "hidden", border: "1px solid #ECECF3",
        boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)", bgcolor: "#fff",
      },
      "& [data-tour='flow-canvas'] .react-flow": {borderRadius: "18px"},
    }}>
      <Project
        key={flowUid}
        project={flowProject}
        openChat={false}
        flowPath={(flowId) => `${basePath}/flow/${flowId}`}
        sideMenuVariant={"panel"}
        sideMenuHeaderAction={<SubProjectSelect/>}
      />
    </Box>
  );
};
